// Third-party packages
import {
	APP_CONF_VARS,
} from '@appConf/vars.conf';
import {
	GetSessionError,
} from '@exceptions/GetSessionError';
import {
	handleErrorResponse,
} from '@modules/utils';
import {
	PRIVATE_ROUTES,
} from '@routes/index';
import {
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import {
	useEffect,
	useState,
} from 'react';
import {
	useDispatch, useSelector,
} from 'react-redux';
import {
	matchRoutes,
	useLocation,
} from 'react-router-dom';

// Project modules
import {
	getSession,
} from '@api/session';
import {
	userLogin, userLogout,
} from '@stores/_slices/user';
import {
	ReducerInstance,
} from '@stores/lpdipro/reducers';

const SESSION_QUERY_KEY = [
	'session',
];

export const useSession = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const queryClient = useQueryClient();

	const user = useSelector((state: ReducerInstance) => state.user?.instance);

	const [
		isInitialized,
		setIsInitialized,
	] = useState(user?.authenticated);

	const {
		data: sessionData,
		error: sessionError,
		isPending: isSessionPending,
		isSuccess: isSessionSuccess,
	} = useQuery({
		enabled: true,
		queryFn: async () => {
			const response = await getSession();
			handleErrorResponse(response, GetSessionError, () => {
				if (user?.authenticated) {
					dispatch(userLogout());
				}
			});
			if (response.status === 200) {
				setIsInitialized(true);
			}
			return response;
		},
		queryKey: SESSION_QUERY_KEY,
		refetchInterval: user?.authenticated ? APP_CONF_VARS.session?.lifetime : false,
		refetchOnMount: user?.authenticated,
		retry: false,
	});

	// Invalidate session on route change
	useEffect(() => {
		if (user?.authenticated) {
			queryClient.invalidateQueries({
				queryKey: SESSION_QUERY_KEY,
			});
		}
	}, [
		location.pathname,
		user?.authenticated,
	]);

	// Handle session success/failure
	useEffect(() => {
		if (sessionData?.status === 200) {
			dispatch(userLogin(sessionData.payload.user));

			if (sessionData.payload?.redirect_url) {
				window.location.href = sessionData.payload.redirect_url;
			}
		}
	}, [
		sessionData,
		dispatch,
	]);

	useEffect(() => {
		const isPrivateRoute = matchRoutes(PRIVATE_ROUTES, location.pathname)?.length ? true : false;
		const hasSessionError = (sessionData && sessionData.status !== 200) || sessionError ? true : false;
		if (hasSessionError && isPrivateRoute && user?.authenticated) {
			dispatch(userLogout());
		}
	}, [
		sessionData,
		sessionError,
		user?.authenticated,
		dispatch,
	]);

	return {
		isAuthenticated: !!user?.authenticated,
		isInitialized,
		isSessionPending,
		isSessionSuccess,
		sessionData,
		sessionError,
	};
};
